import { type ListType, YesNoEnum } from '../models/app';
import { RideType, RideLevel } from '../models/rideType';
import type { TableColumnType } from '../models/table';

export const minutesToAutoRetryRequestTypes: ListType[] = [
  { id: 1, key: 'dashboardNoDriversAvailable', label: 'Driver' },
  { id: 2, key: 'dashboardRidesWithoutSurge', label: 'Ride without Surge' },
];

export const stawavLevelOfServices: ListType[] = [
  { id: 1, key: 'AMB (Ambulatory)', label: 'AMB (Ambulatory)' },
  { id: 2, key: 'WCH (Wheelchair)', label: 'WCH (Wheelchair)' },
];

export const tipTypes: (ListType & { symbol: string })[] = [
  { id: 1, key: 'percent', label: 'Percent of Cost', symbol: '%' },
  { id: 2, key: 'amount', label: 'Fixed Amount', symbol: '$' },
];

export const repeatWeeks: ListType[] = [
  { id: 1, key: 'everyWeek', label: 'Every Week' },
  { id: 2, key: 'twoWeeks', label: 'Two Weeks' },
  { id: 3, key: 'threeWeeks', label: 'Three Weeks' },
  { id: 4, key: 'fourWeeks', label: 'Four Weeks' },
];

export const rideTypes: ListType[] = RideType.ALL.map((rideType, idx) => ({
  id: idx + 1,
  key: rideType.key,
  label: rideType.description,
}));

export const rideHistoryTableColumnsForOperator: TableColumnType[] = [
  { label: 'Customer', field: 'customerName', width: 150 },
  { label: 'Ride ID', field: 'id', width: 100 },
  { label: 'Request ID', field: 'requestId', width: 180 },
  { label: 'Date and Time', field: 'createdAt', width: 180, isDateField: true },
  { label: 'Service Type', field: 'serviceType', width: 120 },
  { label: 'Status', field: 'status', width: 140 },
  { label: 'Paid Status', field: 'paidStatus', width: 120, isCapitalize: true },
  { label: 'Rider Phone', field: 'riderPhone', width: 120 },
  { label: 'Ordered By', field: 'orderedBy', width: 120 },
  { label: 'Canceled By', field: 'canceledBy', width: 120 },
  { label: 'Start Location', field: 'startLocation', width: 220 },
  { label: 'Start Lat', field: 'startLat', width: 100 },
  { label: 'Start Long', field: 'startLong', width: 100 },
  { label: 'Start Name', field: 'startName', width: 220 },
  { label: 'End Location', field: 'endLocation', width: 220 },
  { label: 'End Lat', field: 'endLat', width: 100 },
  { label: 'End Long', field: 'endLong', width: 100 },
  { label: 'Destination', field: 'destination', width: 220 },
  { label: 'Dest Lat', field: 'destinationLat', width: 100 },
  { label: 'Dest Lng', field: 'destinationLng', width: 100 },
  { label: 'Dest Name', field: 'destinationName', width: 220 },
  { label: 'GoGo Fee', field: 'gogoFee', width: 100 },
  { label: 'Total Fare', field: 'gogoCost', width: 100 },
  { label: 'Tip', field: 'tip', width: 80 },
];

export const rideHistoryTableColumnsForPartner: TableColumnType[] = [
  { label: 'No', field: 'index', width: 60, isCenterAlign: true },
  { label: 'Created At', field: 'createdAt', width: 180, isDateField: true },
  { label: 'Status', field: 'status', width: 140 },
  { label: 'Time In Progress', field: 'timeInProgress', width: 180, isDateField: true },
  { label: 'Time Completed', field: 'timeCompleted', width: 180, isDateField: true },
  { label: 'Customer', field: 'customerName', width: 150 },
  { label: 'Date Of Birth', field: 'dob', width: 120 },
  { label: 'Phone Number', field: 'riderPhone', width: 120 },
  { label: 'Request ID', field: 'requestId', width: 280 },
  { label: 'Start Location', field: 'startLocation', width: 270 },
  { label: 'End Location', field: 'endLocation', width: 270 },
  { label: 'Destination', field: 'destination', width: 270 },
  { label: 'Distance', field: 'distance', width: 100 },
  { label: 'Duration', field: 'duration', width: 100 },
  { label: 'Total Fare', field: 'gogoCost', width: 100 },
  { label: 'Trip Fare', field: 'totalFare', width: 100 },
  { label: 'Start Name', field: 'startName', width: 220 },
  { label: 'End Location', field: 'endLocation', width: 220 },
  { label: 'Customer Cost', field: 'customerCost', width: 120 },
  { label: 'Customer Credits', field: 'customerCredits', width: 150 },
  { label: 'Partner Cost', field: 'partnerCost', width: 120 },
  { label: 'Partner Credits', field: 'partnerCredits', width: 150 },
  { label: 'Partner Rides', field: 'partnerRides', width: 120 },
  { label: 'Tip', field: 'tip', width: 80 },
  { label: 'Surge Factor', field: 'surgeFactor', width: 120 },
  { label: 'Applied Subsidy', field: 'appliedSubsidy', width: 120 },
  { label: 'Ride Purpose', field: 'partnerRidePurpose', width: 120 },
];

export const MAX_INTERMEDIATE_STOPS = 3;

export const MULTI_STOP_WAIT_FEE_WARNING = `The first two minutes of wait time at your first stop are free. After that, an additional wait fee of $1 per minute will be passed on to the driver as a tip.\n\nIt may take longer to find a driver who is able to provide a multi-stop ride. For stops over 5 minutes, we recommend booking a separate ride back to save money. Ask if the customer's stop is less than five minutes and proceed only if they acknowledge the wait fee.
`;

export const emptyAddress = {
  streetAddress: '',
  streetAddress1: '',
  city: '',
  state: '',
  zip: '',
  name: '',
  latitude: null,
  longitude: null,
  googlePlaceId: null,
  type: false,
};

export const RETURN_TRIP_ETA_THRESHOLD_MINUTES = 15;

export const RETURN_TRIP_ALERTS = {
  NO_COVERAGE: {
    title: 'No Coverage at Destination',
    message: 'Inform the customer that there no service at their destination. They will not be able to get a ride back from there.',
  },
  LIMITED_COVERAGE: {
    title: 'Limited Coverage at Destination',
    message:
      'Inform the customer that there is limited service at their destination. They might not be able to get a ride back from there or it make take longer to find a driver.',
  },
} as const;

export const rideLevelOrder = [
  RideLevel.car,
  RideLevel.suv,
  RideLevel.van,
  RideLevel.premium,
  RideLevel.lux,
  RideLevel.luxsuv,
  RideLevel.wheelChair,
  RideLevel.espanol,
  RideLevel.assistance,
  RideLevel.delivery,
  RideLevel.other,
];

export const specialRequestOrPharmacyDeliveryOptions: ListType[] = [
  { id: 1, key: 'photoProofRequired', label: 'Photo Proof of Delivery' },
  { id: 2, key: 'signatureProofRequired', label: 'Signature Proof of Delivery' },
];

export const specialRequestItemTypes: ListType[] = [
  { id: 1, key: 'hot_food', label: 'Hot food' },
  { id: 2, key: 'groceries', label: 'Groceries' },
  { id: 3, key: 'package', label: 'Package' },
  { id: 4, key: 'other', label: 'Other' },
];

export const specialRequestPaidOptions: ListType[] = [
  { id: 1, key: YesNoEnum.Yes, label: 'Yes' },
  { id: 2, key: YesNoEnum.No, label: 'No' },
  { id: 3, key: 'noPaymentRequired', label: 'No payment required' },
];
